import { Component } from '@angular/core';
import {
  ProductClassification, ProductType, ProductSpecification, ThemeReplacementTag
} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService, ProductService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'product-categorization',
  templateUrl: './product-categorization.component.html',
  styleUrls: ['./product-categorization.component.less']
})

export class ProductCategorization extends SituBaseComponent {

  productClassifications?: ProductClassification[];
  productTypes?: ProductType[];
  productSpecifications?: ProductSpecification[];
  themeReplacementTags?: ThemeReplacementTag[];

  selectedProductClassificationId?: string;
  selectedProductTypeId?: string;

  selectedProductClassification?: ProductClassification;
  selectedProductType?: ProductType;

  addProductClassificationMode: boolean = false;
  updateProductClassificationMode: boolean = false;

  addProductTypeMode: boolean = false;
  updateProductTypeMode: boolean = false;

  private _loading = 0;
  public get loading(): number { return this._loading; }

  public set loading(val: number) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val != 0);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private productService: ProductService) {
    super(authService, stateService);

    this.getProductClassifications();
    this.getProductTypes();
    this.getProductSpecifications();
    this.getThemeReplacementTags();
  }

  onClassificationChange() {
    var productClassification = this.productClassifications?.find(e => e.id == this.selectedProductClassificationId);
    this.selectedProductClassification = new ProductClassification(productClassification);

    this.selectedProductType = undefined;
    this.selectedProductTypeId = undefined;
  }

  onTypeChange() {
    var productType = this.productTypes?.find(e => e.id == this.selectedProductTypeId);
    this.selectedProductType = new ProductType(productType);
  }

  updateClassificationMode() {
    this.updateProductClassificationMode = true;
  }

  addClassificationMode() {
    this.addProductClassificationMode = true;

    this.selectedProductClassification = new ProductClassification();
    this.selectedProductClassification.id = Guid.create().toString();
    this.selectedProductClassification.name = "New Product Classification";
  }

  cancelAddOrUpdateClassificationMode() {
    this.updateProductClassificationMode = false;
    this.addProductClassificationMode = false;

    this.getProductClassifications();
    this.onClassificationChange();
  }

  addClassification() {
    this.productService.AddProductClassification(this.selectedProductClassification!)
    .then((productClassification: ProductClassification | undefined) => {
      this.getProductClassifications();

      if (!productClassification) {
        this.snackBar.open("Failed to add Product Classification.");
        return;
      }

      this.addProductClassificationMode = false;
      this.selectedProductClassificationId = productClassification.id;
    });
  }

  updateClassification() {
    this.productService.UpdateProductClassification(this.selectedProductClassification!)
    .then((productClassification: ProductClassification | undefined) => {
      this.getProductClassifications();

      if (!productClassification) {
        this.snackBar.open("Failed to update Product Classification.");
        return;
      }

      this.updateProductClassificationMode = false;
      this.selectedProductClassificationId = productClassification.id;
    });
  }

  updateTypeMode() { 
    this.updateProductTypeMode = true;
  }

  addTypeMode() {
    this.addProductTypeMode = true;

    this.selectedProductType = new ProductType();
    this.selectedProductType.id = Guid.create().toString();
    this.selectedProductType.name = "New Product Type";
    this.selectedProductType.templateName = "material | product";
    this.selectedProductType.productClassificationId = this.selectedProductClassificationId;
  }

  cancelAddOrUpdateTypeMode() {
    this.updateProductTypeMode = false;
    this.addProductTypeMode = false;

    this.getProductTypes();
    this.onTypeChange();
  }

  addType() {
    this.productService.AddProductType(this.selectedProductType!)
    .then((productType: ProductType | undefined) => {
      this.getProductTypes();

      if (!productType) {
        this.snackBar.open("Failed to add Product Type.");
        return;
      }

      this.addProductTypeMode = false;
      this.selectedProductTypeId = productType.id;
    });
  }

  updateType() {
    this.productService.UpdateProductType(this.selectedProductType!)
    .then((productType: ProductType | undefined) => {
      this.getProductTypes();

      if (!productType) {
        this.snackBar.open("Failed to update Product Type.");
        return;
      }

      this.updateProductTypeMode = false;
      this.selectedProductTypeId = productType.id;
    });
  }

  addSpecification(productSpecification: ProductSpecification) {
    this.productService.AddProductSpecification(productSpecification)
    .then((productSpecification: ProductSpecification | undefined) => {
      this.getProductSpecifications();

      if (!productSpecification) {
        this.snackBar.open("Failed to add Product Specification.");
        return;
      }
    });
  }

  updateSpecification(productSpecification: ProductSpecification) {
    this.productService.UpdateProductSpecification(productSpecification)
    .then((productSpecification: ProductSpecification | undefined) => {
      this.getProductSpecifications();

      if (!productSpecification) {
        this.snackBar.open("Failed to update Product Specification.");
        return;
      }
    });
  }

  getProductClassifications() {
    this.loading++;
    this.productService.LoadProductClassifications()
    .then((productClassifications) => {
      if (productClassifications.length == 0) {
        this.snackBar.open("Failed to load Product Classifications.");
      }

      // Add a blank classification, for product types that are not classified
      var blankClassification = new ProductClassification({ id: undefined, name: '' });
      this.productClassifications = [blankClassification, ...productClassifications];

      this.loading--;
    }).catch((e) => {
      this.loading--;
      this.snackBar.open("Failed to load Product Classifications.");
    });
  }

  getProductTypes() {
    this.loading++;
    this.productService.LoadProductTypes()
    .then((productTypes) => {
      if (productTypes.length == 0) {
        this.snackBar.open("Failed to load Product Types.");
      }

      this.productTypes = productTypes;

      this.loading--;
    }).catch((e) => {
      this.loading--;
      this.snackBar.open("Failed to load Product Types.");
    });
  }

  getProductSpecifications() {
    this.loading++;
    this.productService.LoadProductSpecifications()
    .then((productSpecifications) => {
      if (productSpecifications.length == 0) {
        this.snackBar.open("Failed to load Product Specifications.");
      }

      this.productSpecifications = productSpecifications;

      this.loading--;
    }).catch((e) => {
      this.loading--;
      this.snackBar.open("Failed to load Product Specifications.");
    });
  }

  getThemeReplacementTags() {
    this.loading++;
    this.productService.LoadThemeReplacementTags(true, false)
    .then((themeReplacementTags) => {
      if (themeReplacementTags.length == 0) {
        this.snackBar.open("Failed to load Replacement Tags.");
      }

      this.themeReplacementTags = themeReplacementTags;

      this.loading--;
    }).catch((e) => {
      this.loading--;
      this.snackBar.open("Failed to load Replacement Tags.");
    });
  }

  filterRelatedReplacementTags(): ThemeReplacementTag[] {
    if (this.themeReplacementTags) {
      return this.themeReplacementTags.filter(e => e.productTypeIds?.includes(this.selectedProductTypeId!))
    } else {
      return [];
    }
  }

  filterNotRelatedReplacementTags(): ThemeReplacementTag[] {
    if (this.themeReplacementTags) {
      return this.themeReplacementTags.filter(e => !e.productTypeIds?.includes(this.selectedProductTypeId!))
    } else {
      return [];
    }
  }

  addRelatedReplacementTag(item: ThemeReplacementTag){
    if(this.selectedProductTypeId && item?.id) {
      item.productTypeIds!.push(this.selectedProductTypeId);
    }
  }

  removeRelatedReplacementTag(item: ThemeReplacementTag){
    if(this.selectedProductTypeId && item?.id) {
      item.productTypeIds = item.productTypeIds!.filter(e => e !== this.selectedProductTypeId)
    }
  }

  updateRelatedReplacementTags() {
    var relatedReplacementTagIds = this.filterRelatedReplacementTags().map(e => e.id!);
    this.productService.UpdateProductTypeRelatedTags(this.selectedProductTypeId!, relatedReplacementTagIds)
    .then((success) => {
      this.getThemeReplacementTags();

      if (!success) {
        this.snackBar.open("Failed to update Product Type Related Tags.");
        return;
      }
    });
  }

  filterTypesByClassification(): ProductType[] {
    if (this.productTypes) {
      return this.productTypes
      .filter(item => item.productClassificationId == this.selectedProductClassificationId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      return [];
    }
  }
}